import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth, ROBYN_PHOTO } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  MessageSquare,
  AlertTriangle,
  HandHelping,
  FileText,
  Send,
  User,
  Clock,
  CheckCircle,
  RefreshCw,
  Plus,
  Shield,
  Wrench,
  Car,
  MapPin,
  Phone
} from 'lucide-react';

const ResidentDashboard = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAssistanceDialog, setShowAssistanceDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [assistanceForm, setAssistanceForm] = useState({
    request_type: '',
    title: '',
    description: '',
    location: '',
    urgency: 'normal',
    for_person_name: '',
    for_person_contact: ''
  });

  const [messageForm, setMessageForm] = useState({
    content: ''
  });

  useEffect(() => {
    if (authLoading) {
      return; // Wait for auth to complete
    }
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchDashboard();
  }, [user, authLoading, navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/my-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAssistanceSubmit = async () => {
    if (!assistanceForm.request_type || !assistanceForm.title || !assistanceForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/assistance-requests`, assistanceForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request submitted successfully');
      setShowAssistanceDialog(false);
      setAssistanceForm({
        request_type: '',
        title: '',
        description: '',
        location: '',
        urgency: 'normal',
        for_person_name: '',
        for_person_contact: ''
      });
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageForm.content.trim()) return;

    setSubmitting(true);
    try {
      // Send to admin (first admin in system)
      const usersRes = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      
      const admin = usersRes.data.find(u => u.is_admin);
      if (!admin) {
        toast.error('No admin available to receive messages');
        return;
      }

      await axios.post(`${API}/messages`, {
        recipient_id: admin.id,
        content: messageForm.content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Message sent to the team!');
      setShowMessageDialog(false);
      setMessageForm({ content: '' });
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-plp-blue" />
      </div>
    );
  }

  const statusColors = {
    pending: 'bg-amber-500',
    in_progress: 'bg-blue-500',
    resolved: 'bg-emerald-500',
    closed: 'bg-slate-500',
    active: 'bg-emerald-500',
    todo: 'bg-slate-500',
    done: 'bg-emerald-500'
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-plp-gold rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-plp-blue" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Welcome, {user.name}!</h1>
              <p className="text-slate-600">Your Killarney Connect Dashboard</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
              <DialogTrigger asChild>
                <Button className="bg-plp-blue hover:bg-plp-blue-light" data-testid="message-team-btn">
                  <MessageSquare className="w-4 h-4 mr-2" /> Message Team
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">Send Message to Robyn's Team</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                    <img src={ROBYN_PHOTO} alt="Robyn" className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <p className="font-medium">Robyn Lyons' Team</p>
                      <p className="text-sm text-slate-500">Your message will be reviewed by the campaign team</p>
                    </div>
                  </div>
                  <Textarea
                    value={messageForm.content}
                    onChange={(e) => setMessageForm({ content: e.target.value })}
                    placeholder="Type your message..."
                    rows={4}
                    data-testid="message-content"
                  />
                  <Button onClick={handleSendMessage} disabled={submitting} className="w-full bg-plp-blue" data-testid="send-message-btn">
                    {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Message
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={showAssistanceDialog} onOpenChange={setShowAssistanceDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="request-assistance-btn">
                  <HandHelping className="w-4 h-4 mr-2" /> Request Assistance
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading">Request Assistance</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
                  <div>
                    <Label>Request Type *</Label>
                    <Select value={assistanceForm.request_type} onValueChange={(v) => setAssistanceForm(p => ({ ...p, request_type: v }))}>
                      <SelectTrigger data-testid="request-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="self">For Myself</SelectItem>
                        <SelectItem value="other">For Someone Else</SelectItem>
                        <SelectItem value="crime">Report Crime</SelectItem>
                        <SelectItem value="suspicious">Suspicious Activity</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {assistanceForm.request_type === 'other' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label>Person's Name</Label>
                        <Input
                          value={assistanceForm.for_person_name}
                          onChange={(e) => setAssistanceForm(p => ({ ...p, for_person_name: e.target.value }))}
                          placeholder="Name"
                        />
                      </div>
                      <div>
                        <Label>Contact Info</Label>
                        <Input
                          value={assistanceForm.for_person_contact}
                          onChange={(e) => setAssistanceForm(p => ({ ...p, for_person_contact: e.target.value }))}
                          placeholder="Phone/Address"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label>Title *</Label>
                    <Input
                      value={assistanceForm.title}
                      onChange={(e) => setAssistanceForm(p => ({ ...p, title: e.target.value }))}
                      placeholder="Brief description"
                      data-testid="assistance-title"
                    />
                  </div>
                  
                  <div>
                    <Label>Details *</Label>
                    <Textarea
                      value={assistanceForm.description}
                      onChange={(e) => setAssistanceForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Provide more details..."
                      rows={3}
                      data-testid="assistance-description"
                    />
                  </div>
                  
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={assistanceForm.location}
                      onChange={(e) => setAssistanceForm(p => ({ ...p, location: e.target.value }))}
                      placeholder="Address or landmark"
                    />
                  </div>
                  
                  <div>
                    <Label>Urgency</Label>
                    <Select value={assistanceForm.urgency} onValueChange={(v) => setAssistanceForm(p => ({ ...p, urgency: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low - Not Urgent</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High - Needs Attention</SelectItem>
                        <SelectItem value="emergency">Emergency - Immediate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button onClick={handleAssistanceSubmit} disabled={submitting} className="w-full bg-plp-blue" data-testid="submit-assistance-btn">
                    {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Submit Request
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{dashboardData?.unread_messages || 0}</p>
                  <p className="text-sm text-slate-500">Unread Messages</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{dashboardData?.my_reports?.length || 0}</p>
                  <p className="text-sm text-slate-500">My Reports</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{dashboardData?.my_issues?.length || 0}</p>
                  <p className="text-sm text-slate-500">My Issues</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <HandHelping className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{dashboardData?.my_requests?.length || 0}</p>
                  <p className="text-sm text-slate-500">Assistance Requests</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Conversations */}
          <div className="lg:col-span-2">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-plp-blue" /> Recent Conversations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!dashboardData?.conversations?.length ? (
                  <div className="text-center py-8 text-slate-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No conversations yet</p>
                    <Button variant="link" onClick={() => setShowMessageDialog(true)} className="text-plp-blue">
                      Start a conversation
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboardData.conversations.map((conv) => (
                      <div key={conv.id || conv.partner?.email} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="w-12 h-12 bg-plp-gold rounded-full flex items-center justify-center">
                          <span className="font-bold text-plp-blue">{conv.partner?.name?.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900">{conv.partner?.name}</p>
                          <p className="text-sm text-slate-500 truncate">{conv.last_message?.content}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {new Date(conv.last_message?.created_at).toLocaleDateString()}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My Assistance Requests */}
            <Card className="bg-white mt-6">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <HandHelping className="w-5 h-5 text-purple-500" /> My Assistance Requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!dashboardData?.my_requests?.length ? (
                  <div className="text-center py-8 text-slate-500">
                    <HandHelping className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No assistance requests</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboardData.my_requests.map((req) => (
                      <div key={req.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <Badge className={`${statusColors[req.status]} text-white mb-2`}>
                              {req.status.replace('_', ' ')}
                            </Badge>
                            <h4 className="font-medium text-slate-900">{req.title}</h4>
                            <p className="text-sm text-slate-600 mt-1">{req.description}</p>
                          </div>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(req.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/safety-map">
                  <Button variant="outline" className="w-full justify-start">
                    <Shield className="w-4 h-4 mr-2 text-blue-500" /> Report Safety Concern
                  </Button>
                </Link>
                <Link to="/fix-it-forward">
                  <Button variant="outline" className="w-full justify-start">
                    <Wrench className="w-4 h-4 mr-2 text-amber-500" /> Report Infrastructure Issue
                  </Button>
                </Link>
                <Link to="/carpool">
                  <Button variant="outline" className="w-full justify-start">
                    <Car className="w-4 h-4 mr-2 text-emerald-500" /> Community Carpool
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* My Recent Reports */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-500" /> My Safety Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!dashboardData?.my_reports?.length ? (
                  <p className="text-center py-4 text-slate-500 text-sm">No reports submitted</p>
                ) : (
                  <div className="space-y-2">
                    {dashboardData.my_reports.slice(0, 3).map((report) => (
                      <div key={report.id} className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium text-sm">{report.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{report.pin_type}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My Issues */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-amber-500" /> My Reported Issues
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!dashboardData?.my_issues?.length ? (
                  <p className="text-center py-4 text-slate-500 text-sm">No issues reported</p>
                ) : (
                  <div className="space-y-2">
                    {dashboardData.my_issues.slice(0, 3).map((issue) => (
                      <div key={issue.id} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm">{issue.title}</p>
                          <Badge className={`${statusColors[issue.status]} text-white text-xs`}>
                            {issue.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResidentDashboard;
